import { useEffect, useRef } from 'react';

/**
 * @hook useLeaderPageStateBackup
 * @description Handles the serialization and restoration of the LeaderPage state via sessionStorage.
 * Prevents infinite rendering loops by using refs for states and executing effects strictly on mount/unmount.
 * 
 * @param {Object} states - Current state values.
 * @param {string} states.mainViewMode - Calendar vs list mode.
 * @param {Date} states.startDate - Current calendar date.
 * @param {string} states.viewMode - Mode of calendar view.
 * @param {number} states.viewDays - Number of days visible.
 * @param {string} states.sidebarMode - Active tab in the sidebar.
 * @param {boolean} states.modalOpen - Is the editor modal open.
 * @param {Object} states.editingAssignment - Current assignment being edited.
 * @param {Date} states.presetDate - Preset date for new assignment.
 * @param {boolean} states.isUndatedPreset - Is the preset date undated.
 * @param {Object} states.activeResources - Resources currently assigned in editor.
 * @param {Object} states.activeDateRange - Active date range in editor.
 * 
 * @param {Object} setters - React state setters to restore state.
 * @param {Function} setters.setMainViewMode
 * @param {Function} setters.setStartDate
 * @param {Function} setters.setViewMode
 * @param {Function} setters.setViewDays
 * @param {Function} setters.setSidebarMode
 * @param {Function} setters.setModalOpen
 * @param {Function} setters.setEditingAssignment
 * @param {Function} setters.setPresetDate
 * @param {Function} setters.setIsUndatedPreset
 * @param {Function} setters.setActiveResources
 * @param {Function} setters.setActiveDateRange
 */
export function useLeaderPageStateBackup(states, setters) {
    const statesRef = useRef(states);

    // Keep ref updated with latest states
    useEffect(() => {
        statesRef.current = states;
    }, [states]);

    // Restoration Effect (runs only once on mount)
    useEffect(() => {
        const backupStr = sessionStorage.getItem('leaderPageStateBackup');
        if (backupStr) {
            try {
                const backup = JSON.parse(backupStr);
                sessionStorage.removeItem('leaderPageStateBackup');

                if (backup.mainViewMode && setters.setMainViewMode) {
                    setters.setMainViewMode(backup.mainViewMode);
                }
                if (backup.startDate && setters.setStartDate) {
                    setters.setStartDate(new Date(backup.startDate));
                }
                if (backup.viewMode && setters.setViewMode) {
                    setters.setViewMode(backup.viewMode);
                }
                if (backup.viewDays && setters.setViewDays) {
                    setters.setViewDays(backup.viewDays);
                }
                if (backup.sidebarMode && setters.setSidebarMode) {
                    setters.setSidebarMode(backup.sidebarMode);
                }
                if (backup.modalOpen && setters.setModalOpen) {
                    setters.setModalOpen(backup.modalOpen);
                }
                if (backup.editingAssignment && setters.setEditingAssignment) {
                    setters.setEditingAssignment(backup.editingAssignment);
                }
                if (backup.presetDate && setters.setPresetDate) {
                    setters.setPresetDate(new Date(backup.presetDate));
                }
                if (backup.isUndatedPreset !== undefined && setters.setIsUndatedPreset) {
                    setters.setIsUndatedPreset(backup.isUndatedPreset);
                }
                if (backup.activeResources && setters.setActiveResources) {
                    setters.setActiveResources(backup.activeResources);
                }
                if (backup.activeDateRange && setters.setActiveDateRange) {
                    setters.setActiveDateRange(backup.activeDateRange);
                }
            } catch (e) {
                console.error('Failed to restore leader page state backup:', e);
            }
        }
    }, []); // Empty dependency array to run only on mount

    // Persistence Effect (runs cleanup only on unmount)
    useEffect(() => {
        return () => {
            const currentStates = statesRef.current;
            const backup = {
                mainViewMode: currentStates.mainViewMode,
                startDate: currentStates.startDate ? currentStates.startDate.toISOString() : null,
                viewMode: currentStates.viewMode,
                viewDays: currentStates.viewDays,
                sidebarMode: currentStates.sidebarMode,
                modalOpen: currentStates.modalOpen,
                editingAssignment: currentStates.editingAssignment,
                presetDate: currentStates.presetDate ? currentStates.presetDate.toISOString() : null,
                isUndatedPreset: currentStates.isUndatedPreset,
                activeResources: currentStates.activeResources,
                activeDateRange: currentStates.activeDateRange
            };
            sessionStorage.setItem('leaderPageStateBackup', JSON.stringify(backup));
        };
    }, []); // Empty dependency array to run cleanup strictly on unmount
}
